import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Divider,
  Alert
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowBack, Edit, Save, Cancel } from '@mui/icons-material';
import dayjs from 'dayjs';
import BankTransaction from '../../models/bankTransaction';
import {
  bankTransactionStatusTranslations,
  getBankTransactionStatusColor,
  bankTransactionTypeTranslations,
  getBankTransactionTypeColor
} from '../../utils/bankTransactionTranslations';
import { jwtDecode } from "jwt-decode";

const BankTransactionDetail = ({ setPageTitle }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [userRole, setUserRole] = useState(null);

  // Set page title
  useEffect(() => {
    if (setPageTitle) {
      setPageTitle('Chi tiết giao dịch ngân hàng');
    }
  }, [setPageTitle]);

  // Check if user is admin
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      const decodedToken = jwtDecode(token);
      setUserRole(decodedToken.role);
      if (decodedToken.role !== 'ADMIN') {
        navigate('/');
      }
    }
  }, [navigate]);

  // Fetch transaction data
  useEffect(() => {
    const fetchTransaction = async () => {
      setLoading(true);
      try {
        const bankTransaction = new BankTransaction();
        const response = await bankTransaction.getBankTransactionById(id);
        
        if (response && response.data) {
          setTransaction(response.data);
          setSelectedStatus(response.data.status);
          setSelectedType(response.data.transaction_type);
        } else {
          setError('Không tìm thấy giao dịch');
        }
      } catch (error) {
        console.error('Error fetching bank transaction:', error);
        setError('Đã xảy ra lỗi khi tải dữ liệu giao dịch');
      } finally {
        setLoading(false);
      }
    };

    fetchTransaction();
  }, [id]);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(amount);
  };

  // Handle save changes
  const handleSave = async () => {
    setSaving(true);
    setSaveSuccess(false);
    
    try {
      const bankTransaction = new BankTransaction({
        ...transaction,
        status: selectedStatus,
        transaction_type: selectedType
      });
      
      const response = await bankTransaction.updateBankTransaction(id);
      
      if (response && response.success) {
        setTransaction(response.data);
        setSaveSuccess(true);
        setEditing(false);
      } else {
        setError('Không thể cập nhật giao dịch');
      }
    } catch (error) {
      console.error('Error updating bank transaction:', error);
      setError('Đã xảy ra lỗi khi cập nhật giao dịch');
    } finally {
      setSaving(false);
    }
  };

  if (userRole !== 'ADMIN') {
    return null;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/bank-transactions')}
          sx={{ mt: 2 }}
        >
          Quay lại danh sách
        </Button>
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 3 }}>
      <Box sx={{ width: '100%', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/bank-transactions')}
          sx={{ mr: 2 }}
        >
          Quay lại
        </Button>
        <Typography variant="h5" component="h1">
          Chi tiết giao dịch
        </Typography>
      </Box>

      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Cập nhật giao dịch thành công
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h6">
            Mã giao dịch: {transaction.code}
          </Typography>
          
          {editing ? (
            <Box>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Save />}
                onClick={handleSave}
                disabled={saving}
                sx={{ mr: 1 }}
              >
                {saving ? 'Đang lưu...' : 'Lưu'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<Cancel />}
                onClick={() => {
                  setEditing(false);
                  setSelectedStatus(transaction.status);
                  setSelectedType(transaction.transaction_type);
                }}
              >
                Hủy
              </Button>
            </Box>
          ) : (
            <Button
              variant="outlined"
              startIcon={<Edit />}
              onClick={() => setEditing(true)}
            >
              Chỉnh sửa
            </Button>
          )}
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary">
              Số tiền
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: transaction.amount > 0 ? 'success.main' : 'error.main',
                fontWeight: 'bold'
              }}
            >
              {formatCurrency(transaction.amount)}
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary">
              Số dư sau giao dịch
            </Typography>
            <Typography variant="h6">
              {formatCurrency(transaction.current_balance)}
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary">
              Ngày giao dịch
            </Typography>
            <Typography variant="body1">
              {transaction.happened_at ? dayjs(transaction.happened_at).format('DD/MM/YYYY HH:mm') : '-'}
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary">
              Loại giao dịch
            </Typography>
            {editing ? (
              <FormControl fullWidth size="small">
                <InputLabel>Loại giao dịch</InputLabel>
                <Select
                  value={selectedType}
                  label="Loại giao dịch"
                  onChange={(e) => setSelectedType(e.target.value)}
                >
                  {Object.entries(bankTransactionTypeTranslations).map(([key, value]) => (
                    <MenuItem key={key} value={key}>
                      {value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Chip
                label={bankTransactionTypeTranslations[transaction.transaction_type] || transaction.transaction_type}
                color={getBankTransactionTypeColor(transaction.transaction_type)}
              />
            )}
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary">
              Trạng thái
            </Typography>
            {editing ? (
              <FormControl fullWidth size="small">
                <InputLabel>Trạng thái</InputLabel>
                <Select
                  value={selectedStatus}
                  label="Trạng thái"
                  onChange={(e) => setSelectedStatus(e.target.value)}
                >
                  {Object.entries(bankTransactionStatusTranslations).map(([key, value]) => (
                    <MenuItem key={key} value={key}>
                      {value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Chip
                label={bankTransactionStatusTranslations[transaction.status] || transaction.status}
                color={getBankTransactionStatusColor(transaction.status)}
              />
            )}
          </Grid>
          
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" color="textSecondary">
              Mô tả
            </Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {transaction.description || 'Không có mô tả'}
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary">
              Ngày tạo
            </Typography>
            <Typography variant="body2">
              {transaction.created_at ? dayjs(transaction.created_at).format('DD/MM/YYYY HH:mm') : '-'}
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary">
              Cập nhật lần cuối
            </Typography>
            <Typography variant="body2">
              {transaction.updated_at ? dayjs(transaction.updated_at).format('DD/MM/YYYY HH:mm') : '-'}
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </Box>
    </Container>
  );
};

export default BankTransactionDetail;
